import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Item 6 — Force-update / cache-bust for Admin & Handler WITHOUT logging out.
 * Auth (localStorage: dws_user / dws_session_id) is intentionally NEVER touched —
 * we only drop the cached frontend code (service-worker + Cache Storage) and hard
 * reload so the very next paint runs the latest deployed bundle.
 */
export const clearAppCachesAndReload = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch (_) { /* ignore */ }
  try {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch (_) { /* ignore */ }
  // Cache-bust the document itself so no intermediate HTTP cache serves the old
  // index.html. Auth stays in localStorage → no logout.
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_v', Date.now().toString());
    window.location.replace(u.toString());
  } catch (_) {
    window.location.reload();
  }
};

// Reads a stable fingerprint of the currently-deployed build. asset-manifest.json
// is regenerated (with new hashed bundle names) on every production build, so a
// change here means a fresh deploy is live.
const readBuildKey = async () => {
  try {
    const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const j = await res.json();
    return JSON.stringify(j.files ? (j.files['main.js'] || j.files) : j);
  } catch (_) {
    return null;
  }
};

// Point 3 — same source as readBuildKey but returns the raw main bundle path
// (e.g. "/static/js/main.<hash>.js") so callers can both COMPARE versions and
// show a short human-readable id. Returns null in dev / when unavailable.
export const readMainBundle = async () => {
  try {
    const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const j = await res.json();
    return (j.files && j.files['main.js']) || (j.entrypoints && j.entrypoints[0]) || null;
  } catch (_) {
    return null;
  }
};

/**
 * Auto cache-bust: polls the deployed build fingerprint and, when it changes
 * (new deploy), refreshes this Admin/Handler session to the latest code.
 * `enabled` should be true ONLY for admin/handler roles.
 */
export const useAutoUpdateCheck = (enabled) => {
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    let baseline = null;
    (async () => { baseline = await readBuildKey(); })();
    const iv = setInterval(async () => {
      if (stop) return;
      const cur = await readBuildKey();
      if (baseline && cur && cur !== baseline) {
        stop = true;
        clearInterval(iv);
        toast.message('Updating to the latest version…', { duration: 1800 });
        // Small delay = a reasonably safe moment; an in-progress unsent draft
        // may be lost (acceptable), but no active action is hard-cut mid-write.
        setTimeout(() => { clearAppCachesAndReload(); }, 1800);
      }
    }, 60000);
    return () => { stop = true; clearInterval(iv); };
  }, [enabled]);
};
